/**
 * `import type`, e não `import`: aqui o Phaser aparece **só** em assinatura — `Phaser.Scene`
 * é o parâmetro de quem publica a textura, e nada neste arquivo chama a engine. Como tipo,
 * ele some na compilação, e quem importa este módulo para apenas **desenhar** — a página
 * inicial em `src/landing.ts`, as prévias de arte — leva 91 kB de desenho em vez de 1,6 MB
 * de engine. O mesmo vale para `art/characters.ts` e `art/creatures.ts`.
 */
import type Phaser from 'phaser';

import { LIGHT_TINT, SHADOW_TINT } from './palette.ts';

/**
 * Utilidades de desenho para os atlas gerados em tempo de execução.
 *
 * A arte do jogo nasce de código, e não de PNGs versionados. A troca é consciente: um
 * atlas desenhado por função é editável por quem não tem ferramenta de pixel art, não
 * pesa no repositório, e cada sprite fica descrito no mesmo lugar em que se explica por
 * que ele é assim. O custo é não ter o detalhe fino de um desenho à mão — o que o PRD
 * §8 explicitamente dispensa ao dizer que a leitura em combate importa mais que a
 * fidelidade de desenho.
 *
 * Substituir por arte de verdade depois é trocar estas funções por `scene.load.atlas`,
 * sem tocar em nada que as consome: quem desenha o jogo enxerga só nomes de textura e
 * de animação.
 */

export function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/**
 * `willReadFrequently` deve ser ligado por quem vai chamar `getImageData` — o contorno
 * dos ícones de item faz isso. Sem a dica, o navegador mantém o canvas na GPU e cada
 * leitura obriga a trazer os pixels de volta, o que ele avisa no console.
 */
export function context2d(
  canvas: HTMLCanvasElement,
  willReadFrequently = false,
): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d', { willReadFrequently });
  if (!ctx) throw new Error('canvas 2d indisponível neste navegador');

  // Nada de suavização: a arte é feita de pixels inteiros, e interpolar borraria o
  // contorno que dá a silhueta.
  ctx.imageSmoothingEnabled = false;
  return ctx;
}

/** Retângulo em coordenadas inteiras. Meio pixel produz borda cinza no canvas. */
export function px(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
}

/** Elipse cheia, para cabeças e corpos arredondados. */
export function ellipse(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
}

// --- Cor -------------------------------------------------------------------

function channels(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function hex(r: number, g: number, b: number): string {
  const clamp = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
  return `#${((clamp(r) << 16) | (clamp(g) << 8) | clamp(b)).toString(16).padStart(6, '0')}`;
}

/** Mistura duas cores. `t` 0 devolve `a`, 1 devolve `b`. */
export function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = channels(a);
  const [br, bg, bb] = channels(b);
  return hex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

/** Clareia (`amount` > 0) ou escurece (< 0) um `#rrggbb`, mantendo o matiz. */
export function shade(color: string, amount: number): string {
  return mix(color, amount < 0 ? '#000000' : '#ffffff', Math.abs(amount));
}

/**
 * Sombra de anime: escurece **e** esfria.
 *
 * A diferença com `shade(cor, -x)` é o segundo passo. Escurecer sozinho devolve a mesma
 * tinta com menos luz, que é o que uma superfície de plástico faz; aqui o tom também
 * caminha para o azul-violeta do céu (`SHADOW_TINT`), que é de onde vem a luz que sobra
 * onde o sol não bate. É a razão de a arte parecer iluminada em vez de pintada.
 *
 * A mistura com o tinte é bem mais fraca que o escurecimento, e o fator é baixo por
 * medição, não por gosto: com 0,55 a junta do calçamento e o vão entre as pedras da parede
 * saíam **lilás**, e como junta e vão respondem por perto de um décimo da área de cada
 * tile, a praça inteira e a muralha inteira ganhavam um viés frio que a paleta não pedia.
 * Sombra que puxa demais para o roxo perde a identidade do material, e uma parede na
 * sombra ainda precisa ser lida como pedra.
 */
export function celShadow(color: string, amount = 0.32): string {
  return mix(shade(color, -amount), SHADOW_TINT, amount * 0.4);
}

/** Realce de anime: clareia e esquenta, na direção da luz do sol (`LIGHT_TINT`). */
export function celLight(color: string, amount = 0.26): string {
  return mix(shade(color, amount * 0.55), LIGHT_TINT, amount);
}

// --- Formas ----------------------------------------------------------------

/**
 * Retângulo de cantos arredondados.
 *
 * Existe porque a queixa de "está tudo muito quadrado" é literal: membro, laje e tijolo
 * eram `fillRect`, e um raio de um único pixel na quina já é a diferença entre uma peça
 * desenhada e um retângulo pintado. Em pixel art o raio é pequeno por obrigação — dois
 * pixels num tile de 32 é o máximo antes de a forma virar pastilha.
 */
export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
  color: string,
): void {
  ctx.fillStyle = color;
  roundRectPath(ctx, x, y, w, h, radius);
  ctx.fill();
}

/** O caminho de `roundRect`, sem pintar. Para quem precisa recortar ou contornar. */
export function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
): void {
  const r = Math.max(0, Math.min(radius, Math.min(w, h) / 2));
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * Silhueta em lóbulos: um contorno fechado feito de arcos de tamanhos diferentes.
 *
 * É a forma da folhagem de desenho japonês — não uma bola, e também não uma bola
 * serrilhada, mas um **agregado de massas** cujas bordas se leem como tufos. A versão
 * anterior chegava perto empilhando elipses e depois salpicando pixels na borda; o
 * problema é que o salpico é ruído, e ruído não tem silhueta. Aqui a irregularidade está
 * no próprio caminho, então o contorno que `outline()` extrai depois já sai recortado.
 *
 * `lobes` é quantas massas compõem a volta, e `seed` sorteia o tamanho de cada uma —
 * estável, para que a mesma variação de árvore saia igual em toda sessão.
 */
export function cloud(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  lobes: number,
  seed: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();

  for (let i = 0; i < lobes; i++) {
    const angle = (i / lobes) * Math.PI * 2;
    // Ruído inteiro barato, na mesma linhagem do `hash01` do tileset: o requisito é ser
    // estável, não ser bem distribuído.
    const n = Math.imul(i + seed * 131, 374_761_393);
    const jitter = 0.78 + (((n ^ (n >>> 13)) >>> 0) % 1000) / 1000 * 0.42;

    const lx = cx + Math.cos(angle) * rx * 0.62;
    const ly = cy + Math.sin(angle) * ry * 0.62;
    const lr = Math.min(rx, ry) * 0.52 * jitter;

    ctx.moveTo(lx + lr, ly);
    ctx.arc(lx, ly, lr, 0, Math.PI * 2);
  }

  // O miolo, para que os lóbulos não deixem um vazio no centro.
  ctx.moveTo(cx + rx * 0.66, cy);
  ctx.ellipse(cx, cy, rx * 0.66, ry * 0.66, 0, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Recorta o que for desenhado em `draw` à forma que `mask` desenhar.
 *
 * É como se faz sombra de borda dura: a banda de sombra é um retângulo grande, e a forma
 * do corpo é que decide onde ela aparece. Pintar a sombra "dentro" de cada peça exigiria
 * repetir a geometria de cada peça duas vezes, e as duas cópias divergiriam no primeiro
 * ajuste.
 */
export function clipped(
  ctx: CanvasRenderingContext2D,
  mask: Shape,
  draw: (c: CanvasRenderingContext2D) => void,
): void {
  ctx.save();
  ctx.beginPath();
  mask(ctx);
  ctx.clip();
  draw(ctx);
  ctx.restore();
}

/** Uma forma: acrescenta um caminho fechado ao contexto, sem pintar. */
export type Shape = (ctx: CanvasRenderingContext2D) => void;

/**
 * Uma massa com sombra de borda dura, do jeito que o desenho japonês faz.
 *
 * A sombra **não** é uma segunda forma desenhada à mão: é a própria massa, pintada duas
 * vezes. Primeiro tudo na cor de sombra; depois a mesma forma deslocada na direção do sol
 * — para cima e para a esquerda, como em toda a arte do jogo — e recortada pela primeira.
 * O que sobra descoberto é uma faixa de sombra que acompanha a silhueta exatamente,
 * porque *é* a silhueta, com a borda dura que define cel shading.
 *
 * A vantagem sobre desenhar a sombra à parte é que ela não pode divergir da forma: mudar
 * a curva do ombro reposiciona a sombra do ombro no mesmo instante, de graça.
 *
 * `light` acrescenta a luz de contorno — um traço claro só na beira voltada para o sol,
 * obtido estampando a forma deslocada **contra** ele e aparando o que sair fora.
 */
export function celMass(
  ctx: CanvasRenderingContext2D,
  shape: Shape,
  base: string,
  options: { depth?: number; light?: string; dx?: number; dy?: number } = {},
): void {
  const { depth = 0.32, light, dx = -2, dy = -1 } = options;

  ctx.fillStyle = celShadow(base, depth);
  shape(ctx);
  ctx.fill();

  clipped(ctx, shape, (c) => {
    c.translate(dx, dy);
    c.fillStyle = base;
    shape(c);
    c.fill();
  });

  if (light === undefined) return;

  clipped(ctx, shape, (c) => {
    c.translate(-dx * 0.35, -dy * 0.45);
    c.strokeStyle = light;
    c.lineWidth = 1.4;
    shape(c);
    c.stroke();
  });
}

/**
 * Registra o canvas como textura e recorta os quadros numa grade.
 *
 * A grade é o que permite ao Phaser tratar o desenho como spritesheet: cada quadro vira
 * um índice, e as animações se referem a esses índices.
 */
export function addGridTexture(
  scene: Phaser.Scene,
  key: string,
  canvas: HTMLCanvasElement,
  frameWidth: number,
  frameHeight: number,
  columns: number,
  rows: number,
): void {
  if (scene.textures.exists(key)) return;

  const texture = scene.textures.addCanvas(key, canvas);
  if (!texture) return;

  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      texture.add(
        row * columns + column,
        0,
        column * frameWidth,
        row * frameHeight,
        frameWidth,
        frameHeight,
      );
    }
  }
}

/**
 * Contorno escuro em volta do que foi desenhado no canvas.
 *
 * Percorre os pixels transparentes e pinta os que encostam em algo opaco. Uma passada por
 * quadro, uma vez na entrada.
 *
 * O ganho não é só separar o desenho do fundo — é ter **um** contorno em vez de um por
 * peça. Um bicho montado de elipses contornadas uma a uma ganha anéis escuros no meio do
 * corpo, e o que se vê são balões costurados; com o contorno tirado da silhueta pronta, o
 * mesmo desenho vira um corpo só. É a diferença entre "várias formas" e "uma criatura".
 */
export function outline(ctx: CanvasRenderingContext2D, color: string): void {
  const { width, height } = ctx.canvas;
  const image = ctx.getImageData(0, 0, width, height);
  const data = image.data;

  const alphaAt = (x: number, y: number): number => {
    if (x < 0 || y < 0 || x >= width || y >= height) return 0;
    return data[(y * width + x) * 4 + 3] ?? 0;
  };

  const target = ctx.createImageData(image);
  target.data.set(data);

  const value = Number.parseInt(color.slice(1), 16);
  const [r, g, b] = [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (alphaAt(x, y) !== 0) continue;

      const touching =
        alphaAt(x - 1, y) || alphaAt(x + 1, y) || alphaAt(x, y - 1) || alphaAt(x, y + 1);
      if (!touching) continue;

      const index = (y * width + x) * 4;
      target.data[index] = r;
      target.data[index + 1] = g;
      target.data[index + 2] = b;
      target.data[index + 3] = 255;
    }
  }

  ctx.putImageData(target, 0, 0);
}

/** Textura de quadro único, para ícones e tiles. */
export function addTexture(
  scene: Phaser.Scene,
  key: string,
  canvas: HTMLCanvasElement,
): void {
  if (!scene.textures.exists(key)) scene.textures.addCanvas(key, canvas);
}
